import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  SafeAreaView,
  ScrollView,
  TextInput,
  Platform,
  Share,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useIconTheme } from '../contexts/IconThemeContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../services/AuthContext';
import { 
  listHotspotsAdmin, 
  bulkUpdateHotspots,
  deleteHotspot,
} from '../services/database';

// Constants
const HOTSPOT_CATEGORIES = ['PARK', 'CAFE', 'BAR', 'CLUB', 'UNIVERSITY', 'PUBLIC_SQUARE', 'LIBRARY', 'OTHER'];
const HOTSPOT_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
const HOTSPOT_CONFIDENCES = ['CONFIRMED', 'KNOWN_HOTSPOT', 'RELATED'];
const HOTSPOT_SOURCES = ['ADMIN', 'USER', 'AI'];

const AdminHotspotsListScreen = ({ navigation }) => {
  const { getCategoryIcon } = useIconTheme();
  const { showToast } = useToast();
  const { appUser } = useAuth();
  
  // Data state
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: null,
    category: null,
    confidence: null,
    source: null,
    linksVetted: null,
    hasSocialLink: null,
    searchQuery: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  
  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  // Single hotspot delete (from row action)
  const [hotspotToDelete, setHotspotToDelete] = useState(null);
  const [singleDeleteConfirmText, setSingleDeleteConfirmText] = useState('');
  // Download list modal
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  // Check if user is admin
  const isAdmin = appUser?.isAdmin === true;

  useEffect(() => {
    if (!isAdmin) {
      showToast('Access denied. Admin privileges required.', 'error');
      navigation.goBack();
      return;
    }
    fetchHotspots();
  }, [isAdmin]);

  // Refresh when screen comes into focus
  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', () => {
      if (isAdmin) fetchHotspots();
    });
    return unsubscribe;
  }, [navigation, isAdmin]);

  const fetchHotspots = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedHotspots = await listHotspotsAdmin(filters);
      setHotspots(fetchedHotspots);
    } catch (error) {
      console.error('Error fetching hotspots:', error);
      showToast('Failed to load hotspots', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isAdmin) fetchHotspots();
  }, [filters, isAdmin]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchHotspots();
  };

  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    setSelectedIds(new Set());
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    if (selectedIds.size === hotspots.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(hotspots.map(h => h.id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.size === 0) {
      showToast('No hotspots selected', 'warning');
      return;
    }

    if (action === 'DELETE') {
      setShowDeleteModal(true);
      return;
    }

    try {
      setActionInProgress(true);
      const ids = Array.from(selectedIds);
      await bulkUpdateHotspots(ids, action);
      showToast(`${action.toLowerCase()} ${ids.length} hotspot(s) successfully`, 'success');
      setSelectedIds(new Set());
      setSelectMode(false);
      fetchHotspots();
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      showToast(`Failed to ${action.toLowerCase()} hotspots`, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showToast('Please type DELETE to confirm', 'warning');
      return;
    }

    try {
      setActionInProgress(true);
      const ids = Array.from(selectedIds);
      await bulkUpdateHotspots(ids, 'DELETE', 'DELETE');
      showToast(`Deleted ${ids.length} hotspot(s) successfully`, 'success');
      setSelectedIds(new Set());
      setSelectMode(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      fetchHotspots();
    } catch (error) {
      console.error('Error deleting hotspots:', error);
      showToast('Failed to delete hotspots', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const openSingleDelete = (hotspot) => {
    setHotspotToDelete(hotspot);
    setSingleDeleteConfirmText('');
  };

  const confirmSingleDelete = async () => {
    if (singleDeleteConfirmText !== 'DELETE' || !hotspotToDelete) {
      showToast('Type DELETE to confirm', 'warning');
      return;
    }
    try {
      setActionInProgress(true);
      await deleteHotspot(hotspotToDelete.id);
      showToast(`Deleted "${hotspotToDelete.name}"`, 'success');
      setHotspotToDelete(null);
      setSingleDeleteConfirmText('');
      fetchHotspots();
    } catch (error) {
      console.error('Error deleting hotspot:', error);
      showToast(error?.message || 'Failed to delete hotspot', 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleExport = async () => {
    const selectedHotspots = hotspots.filter(h => selectedIds.has(h.id));
    const exportData = JSON.stringify(selectedHotspots, null, 2);
    
    try {
      if (Platform.OS === 'web') {
        const blob = new Blob([exportData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `hotspots-export-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
        showToast(`Exported ${selectedHotspots.length} hotspot(s)`, 'success');
      } else {
        await Share.share({
          message: exportData,
          title: 'Hotspots Export',
        });
      }
    } catch (error) {
      console.error('Error exporting:', error);
      showToast('Failed to export hotspots', 'error');
    }
  };

  // Escape CSV value (wrap in quotes, escape internal quotes)
  const escapeCsv = (val) => {
    if (val == null) return '';
    const s = String(val).replace(/"/g, '""');
    return /[",\n\r]/.test(s) ? `"${s}"` : s;
  };

  const hotspotsToCsv = (list) => {
    const headers = ['name', 'address', 'city', 'category', 'description', 'status', 'latitude', 'longitude', 'instagram_url', 'facebook_url', 'twitter_url', 'confidence', 'source', 'created_at'];
    const rows = list.map(h => headers.map(col => escapeCsv(h[col])));
    return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
  };

  const downloadListAs = async (format) => {
    setShowDownloadModal(false);
    const list = hotspots;
    if (list.length === 0) {
      showToast('No hotspots to download', 'warning');
      return;
    }
    const dateStr = new Date().toISOString().split('T')[0];
    try {
      if (format === 'json') {
        const content = JSON.stringify(list, null, 2);
        const mime = 'application/json';
        const filename = `hotspots-list-${dateStr}.json`;
        if (Platform.OS === 'web') {
          const blob = new Blob([content], { type: mime });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          await Share.share({ message: content, title: filename });
        }
        showToast(`Downloaded ${list.length} hotspot(s) as JSON`, 'success');
      } else if (format === 'csv') {
        const content = hotspotsToCsv(list);
        const mime = 'text/csv;charset=utf-8';
        const filename = `hotspots-list-${dateStr}.csv`;
        if (Platform.OS === 'web') {
          const blob = new Blob([content], { type: mime });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          await Share.share({ message: content, title: filename });
        }
        showToast(`Downloaded ${list.length} hotspot(s) as CSV`, 'success');
      }
    } catch (error) {
      console.error('Error downloading list:', error);
      showToast('Failed to download list', 'error');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      PARK: 'Park',
      CAFE: 'Cafe',
      BAR: 'Bar',
      CLUB: 'Chess Club',
      UNIVERSITY: 'University',
      PUBLIC_SQUARE: 'Public Square',
      LIBRARY: 'Library',
      OTHER: 'Other',
    };
    return labels[category] || category;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PUBLISHED': return '#4CAF50';
      case 'DRAFT': return '#FF9800';
      case 'ARCHIVED': return '#9E9E9E';
      default: return '#666';
    }
  };

  const getConfidenceLabel = (confidence) => {
    switch (confidence) {
      case 'CONFIRMED': return '✓ Confirmed';
      case 'KNOWN_HOTSPOT': return '● Known';
      case 'RELATED': return '○ Related';
      default: return confidence;
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'ADMIN': return '👤';
      case 'USER': return '👥';
      case 'AI': return '🤖';
      default: return '?';
    }
  };

  const renderFilterChip = (label, value, options, onChange, optionLabels = null) => (
    <View style={styles.filterChipContainer}>
      <Text style={styles.filterChipLabel}>{label}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <TouchableOpacity
          style={[styles.filterChip, !value && styles.filterChipActive]}
          onPress={() => onChange(null)}
        >
          <Text style={[styles.filterChipText, !value && styles.filterChipTextActive]}>All</Text>
        </TouchableOpacity>
        {options.map(opt => (
          <TouchableOpacity
            key={opt}
            style={[styles.filterChip, value === opt && styles.filterChipActive]}
            onPress={() => onChange(opt)}
          >
            <Text style={[styles.filterChipText, value === opt && styles.filterChipTextActive]}>
              {optionLabels && optionLabels[opt] != null ? optionLabels[opt] : opt}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderHotspotItem = ({ item }) => {
    const isSelected = selectedIds.has(item.id);
    
    return (
      <TouchableOpacity
        style={[styles.hotspotCard, isSelected && styles.hotspotCardSelected]}
        onPress={() => selectMode ? toggleSelection(item.id) : navigation.navigate('AddEditHotspot', { hotspot: item })}
        onLongPress={() => !selectMode && toggleSelectMode() || toggleSelection(item.id)}
        activeOpacity={0.7}
      >
        {selectMode && (
          <View style={[styles.checkbox, isSelected && styles.checkboxSelected]}>
            {isSelected && <Icon name="check" size={18} color="white" />}
          </View>
        )}
        
        <View style={styles.hotspotHeader}>
          <View style={styles.hotspotIconContainer}>
            <Text style={styles.hotspotIcon}>{getCategoryIcon(item.category)}</Text>
          </View>
          <View style={styles.hotspotInfo}>
            <Text style={styles.hotspotName}>{item.name}</Text>
            <View style={styles.tagsRow}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
              <View style={styles.categoryBadge}>
                <Text style={styles.categoryText}>{getCategoryLabel(item.category)}</Text>
              </View>
              <Text style={styles.sourceIcon}>{getSourceIcon(item.source)}</Text>
              {(() => {
                const hasAny = (item.instagram_url && item.instagram_url.trim()) || (item.facebook_url && item.facebook_url.trim()) || (item.twitter_url && item.twitter_url.trim());
                if (item.links_vetted) return <View style={[styles.linksBadge, styles.linksBadgeVetted]}><Icon name="check-circle" size={14} color="#2E7D32" /></View>;
                if (hasAny) return <View style={[styles.linksBadge, styles.linksBadgeUnvetted]}><Icon name="lens" size={10} color="#F9A825" /></View>;
                return <View style={[styles.linksBadge, styles.linksBadgeNone]}><Icon name="remove" size={14} color="#9e9e9e" /></View>;
              })()}
            </View>
            <Text style={styles.hotspotAddress} numberOfLines={1}>
              📍 {item.address}
            </Text>
            {item.confidence && (
              <Text style={styles.confidenceText}>{getConfidenceLabel(item.confidence)}</Text>
            )}
          </View>
        </View>

        <View style={styles.hotspotMeta}>
          <Text style={styles.metaText}>
            Created: {new Date(item.created_at).toLocaleDateString()}
          </Text>
          <View style={styles.metaActions}>
            {item.import_batch_id && (
              <TouchableOpacity 
                style={styles.batchLink}
                onPress={() => navigation.navigate('ImportBatches', { batchId: item.import_batch_id })}
              >
                <Text style={styles.batchLinkText}>View Batch</Text>
              </TouchableOpacity>
            )}
            {!selectMode && (
              <TouchableOpacity
                style={styles.rowDeleteButton}
                onPress={(e) => { e?.stopPropagation?.(); openSingleDelete(item); }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Icon name="delete-outline" size={22} color="#d32f2f" />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.accessDenied}>
          <Icon name="lock" size={64} color="#ccc" />
          <Text style={styles.accessDeniedText}>Admin Access Required</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Admin Hotspots</Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowDownloadModal(true)}
            accessibilityLabel="Download list"
          >
            <Icon name="file-download" size={24} color="#2E7D32" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => navigation.navigate('AdminHotspotsMap')}
          >
            <Icon name="map" size={24} color="#2196F3" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => navigation.navigate('ImportBatches')}
          >
            <Icon name="history" size={24} color="#9C27B0" />
          </TouchableOpacity>
        </View>
      </View>

      {/* Search Bar */}
      <View style={styles.searchContainer}>
        <Icon name="search" size={20} color="#666" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search name or address..."
          value={filters.searchQuery}
          onChangeText={(text) => setFilters({ ...filters, searchQuery: text })}
        />
        {filters.searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setFilters({ ...filters, searchQuery: '' })}>
            <Icon name="clear" size={20} color="#666" />
          </TouchableOpacity>
        )}
        <TouchableOpacity 
          style={[styles.filterToggle, showFilters && styles.filterToggleActive]}
          onPress={() => setShowFilters(!showFilters)}
        >
          <Icon name="filter-list" size={20} color={showFilters ? '#fff' : '#666'} />
        </TouchableOpacity>
      </View>

      {/* Filters Panel */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          {renderFilterChip('Status', filters.status, HOTSPOT_STATUSES, 
            (val) => setFilters({ ...filters, status: val }))}
          {renderFilterChip('Category', filters.category, HOTSPOT_CATEGORIES,
            (val) => setFilters({ ...filters, category: val }))}
          {renderFilterChip('Confidence', filters.confidence, HOTSPOT_CONFIDENCES,
            (val) => setFilters({ ...filters, confidence: val }))}
          {renderFilterChip('Source', filters.source, HOTSPOT_SOURCES,
            (val) => setFilters({ ...filters, source: val }))}
          {renderFilterChip('Links', filters.linksVetted, ['unvetted', 'vetted', 'no_links'],
            (val) => setFilters({ ...filters, linksVetted: val }),
            { unvetted: 'Unvetted', vetted: 'Vetted', no_links: 'No Links' })}
          {renderFilterChip('Has link', filters.hasSocialLink, ['instagram', 'twitter', 'facebook'],
            (val) => setFilters({ ...filters, hasSocialLink: val }),
            { instagram: 'Has Instagram', twitter: 'Has Twitter', facebook: 'Has Facebook' })}
        </View>
      )}

      {/* Selection Mode Bar */}
      {selectMode && (
        <View style={styles.selectionBar}>
          <TouchableOpacity style={styles.selectAllButton} onPress={selectAll}>
            <Icon 
              name={selectedIds.size === hotspots.length ? "check-box" : "check-box-outline-blank"} 
              size={20} 
              color="#333" 
            />
            <Text style={styles.selectAllText}>
              {selectedIds.size} selected
            </Text>
          </TouchableOpacity>
          <View style={styles.bulkActions}>
            <TouchableOpacity 
              style={[styles.bulkButton, styles.publishButton]}
              onPress={() => handleBulkAction('PUBLISH')}
              disabled={actionInProgress}
            >
              <Icon name="visibility" size={18} color="#fff" />
              <Text style={styles.bulkButtonText}>Publish</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.bulkButton, styles.archiveButton]}
              onPress={() => handleBulkAction('ARCHIVE')}
              disabled={actionInProgress}
            >
              <Icon name="archive" size={18} color="#fff" />
              <Text style={styles.bulkButtonText}>Archive</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.bulkButton, styles.exportButton]}
              onPress={handleExport}
              disabled={actionInProgress}
            >
              <Icon name="file-download" size={18} color="#fff" />
              <Text style={styles.bulkButtonText}>Export</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.bulkButton, styles.deleteButton]}
              onPress={() => handleBulkAction('DELETE')}
              disabled={actionInProgress}
            >
              <Icon name="delete" size={18} color="#fff" />
              <Text style={styles.bulkButtonText}>Delete</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity style={styles.cancelSelectButton} onPress={toggleSelectMode}>
            <Icon name="close" size={20} color="#666" />
          </TouchableOpacity>
        </View>
      )}

      {/* Results count */}
      {!loading && (
        <View style={styles.resultsBar}>
          <Text style={styles.resultsText}>{hotspots.length} hotspot(s) found</Text>
          {!selectMode && (
            <TouchableOpacity style={styles.selectModeButton} onPress={toggleSelectMode}>
              <Icon name="checklist" size={18} color="#2196F3" />
              <Text style={styles.selectModeButtonText}>Select</Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Content */}
      {loading ? (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={300} />
        </View>
      ) : hotspots.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Icon name="place" size={80} color="#ccc" />
          <Text style={styles.emptyText}>No hotspots match your filters</Text>
          <TouchableOpacity 
            style={styles.clearFiltersButton}
            onPress={() => setFilters({ status: null, category: null, confidence: null, source: null, linksVetted: null, hasSocialLink: null, searchQuery: '' })}
          >
            <Text style={styles.clearFiltersText}>Clear Filters</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={hotspots}
          renderItem={renderHotspotItem}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />
      )}

      {/* Download list format modal */}
      {showDownloadModal && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => setShowDownloadModal(false)}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="file-download" size={48} color="#2E7D32" />
              <Text style={styles.modalTitle}>Download hotspot list</Text>
            </View>
            <Text style={styles.modalText}>
              Export the current list ({hotspots.length} hotspot{hotspots.length !== 1 ? 's' : ''}) to share with your partner.
            </Text>
            <View style={styles.downloadFormatButtons}>
              <TouchableOpacity
                style={styles.downloadFormatButton}
                onPress={() => downloadListAs('json')}
              >
                <Icon name="code" size={24} color="#2196F3" />
                <Text style={styles.downloadFormatLabel}>JSON</Text>
                <Text style={styles.downloadFormatHint}>Full data, good for systems</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.downloadFormatButton}
                onPress={() => downloadListAs('csv')}
              >
                <Icon name="table-chart" size={24} color="#4CAF50" />
                <Text style={styles.downloadFormatLabel}>CSV</Text>
                <Text style={styles.downloadFormatHint}>Spreadsheet-friendly</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => setShowDownloadModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Single hotspot delete confirmation */}
      {hotspotToDelete && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity
            style={styles.modalBackdrop}
            activeOpacity={1}
            onPress={() => { setHotspotToDelete(null); setSingleDeleteConfirmText(''); }}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="warning" size={48} color="#f44336" />
              <Text style={styles.modalTitle}>Delete hotspot?</Text>
            </View>
            <Text style={styles.modalText}>
              Permanently delete <Text style={styles.deleteWord}>{hotspotToDelete.name}</Text>? This cannot be undone.
            </Text>
            <Text style={styles.modalText}>
              Type <Text style={styles.deleteWord}>DELETE</Text> to confirm:
            </Text>
            <TextInput
              style={styles.deleteInput}
              value={singleDeleteConfirmText}
              onChangeText={setSingleDeleteConfirmText}
              placeholder="Type DELETE"
              autoCapitalize="characters"
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => { setHotspotToDelete(null); setSingleDeleteConfirmText(''); }}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.confirmDeleteButton, singleDeleteConfirmText !== 'DELETE' && styles.buttonDisabled]}
                onPress={confirmSingleDelete}
                disabled={singleDeleteConfirmText !== 'DELETE' || actionInProgress}
              >
                {actionInProgress ? (
                  <LoadingAnimation size={24} />
                ) : (
                  <Text style={styles.confirmDeleteButtonText}>Delete</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* Delete Confirmation Modal (bulk) */}
      {showDeleteModal && (
        <View style={styles.modalOverlay}>
          <TouchableOpacity 
            style={styles.modalBackdrop} 
            activeOpacity={1} 
            onPress={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          />
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Icon name="warning" size={48} color="#f44336" />
              <Text style={styles.modalTitle}>Confirm Delete</Text>
            </View>
            <Text style={styles.modalText}>
              You are about to permanently delete {selectedIds.size} hotspot(s). This action cannot be undone.
            </Text>
            <Text style={styles.modalText}>
              Type <Text style={styles.deleteWord}>DELETE</Text> to confirm:
            </Text>
            <TextInput
              style={styles.deleteInput}
              value={deleteConfirmText}
              onChangeText={setDeleteConfirmText}
              placeholder="Type DELETE"
              autoCapitalize="characters"
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.confirmDeleteButton, deleteConfirmText !== 'DELETE' && styles.buttonDisabled]}
                onPress={confirmDelete}
                disabled={deleteConfirmText !== 'DELETE' || actionInProgress}
              >
                {actionInProgress ? (
                  <LoadingAnimation size={24} />
                ) : (
                  <Text style={styles.confirmDeleteButtonText}>Delete Forever</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}

      {/* FAB to add new hotspot */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => navigation.navigate('AddEditHotspot', { hotspot: null })}
      >
        <Icon name="add" size={28} color="#fff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    flex: 1,
    textAlign: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerButton: {
    padding: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  filterToggle: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
  },
  filterToggleActive: {
    backgroundColor: '#2196F3',
  },
  filtersPanel: {
    backgroundColor: 'white',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  filterChipContainer: {
    marginBottom: 12,
  },
  filterChipLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#666',
    marginBottom: 6,
  },
  filterChip: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    backgroundColor: '#f0f0f0',
    marginRight: 8,
  },
  filterChipActive: {
    backgroundColor: '#2196F3',
  },
  filterChipText: {
    fontSize: 13,
    color: '#666',
  },
  filterChipTextActive: {
    color: 'white',
    fontWeight: '600',
  },
  selectionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#90CAF9',
  },
  selectAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  selectAllText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  bulkActions: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  bulkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    gap: 4,
  },
  publishButton: {
    backgroundColor: '#4CAF50',
  },
  archiveButton: {
    backgroundColor: '#9E9E9E',
  },
  exportButton: {
    backgroundColor: '#2196F3',
  },
  deleteButton: {
    backgroundColor: '#f44336',
  },
  bulkButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: 'white',
  },
  cancelSelectButton: {
    padding: 4,
  },
  resultsBar: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#fafafa',
  },
  resultsText: {
    fontSize: 13,
    color: '#666',
  },
  selectModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  selectModeButtonText: {
    fontSize: 13,
    color: '#2196F3',
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyText: {
    marginTop: 16,
    fontSize: 16,
    color: '#999',
    textAlign: 'center',
  },
  clearFiltersButton: {
    marginTop: 16,
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#2196F3',
    borderRadius: 8,
  },
  clearFiltersText: {
    color: 'white',
    fontWeight: '600',
  },
  listContent: {
    padding: 16,
    paddingBottom: 100,
  },
  hotspotCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    flexDirection: 'column',
  },
  hotspotCardSelected: {
    backgroundColor: '#E3F2FD',
    borderColor: '#2196F3',
    borderWidth: 2,
  },
  checkbox: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#ccc',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  checkboxSelected: {
    backgroundColor: '#2196F3',
    borderColor: '#2196F3',
  },
  hotspotHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  hotspotIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: '#FFF3E0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  hotspotIcon: {
    fontSize: 28,
  },
  hotspotInfo: {
    flex: 1,
  },
  hotspotName: {
    fontSize: 17,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: 'white',
  },
  categoryBadge: {
    backgroundColor: '#FF9800',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '600',
    color: 'white',
  },
  sourceIcon: {
    fontSize: 14,
  },
  linksBadge: {
    marginLeft: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  linksBadgeVetted: {},
  linksBadgeUnvetted: {},
  linksBadgeNone: {},
  hotspotAddress: {
    fontSize: 13,
    color: '#666',
  },
  confidenceText: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
  },
  hotspotMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  metaText: {
    fontSize: 12,
    color: '#999',
  },
  metaActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  rowDeleteButton: {
    padding: 4,
  },
  batchLink: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
  },
  batchLinkText: {
    fontSize: 11,
    color: '#4CAF50',
    fontWeight: '600',
  },
  accessDenied: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 16,
  },
  accessDeniedText: {
    fontSize: 18,
    color: '#999',
  },
  fab: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
  },
  modalOverlay: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    zIndex: 1001,
    ...Platform.select({
      web: {
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 20,
      },
    }),
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginTop: 8,
  },
  modalText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: 12,
    lineHeight: 20,
  },
  deleteWord: {
    fontWeight: 'bold',
    color: '#f44336',
  },
  deleteInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    marginTop: 8,
  },
  downloadFormatButtons: {
    flexDirection: 'row',
    gap: 12,
    marginVertical: 16,
  },
  downloadFormatButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f5f5f5',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  downloadFormatLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
    marginTop: 8,
  },
  downloadFormatHint: {
    fontSize: 11,
    color: '#666',
    marginTop: 2,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  confirmDeleteButton: {
    flex: 1,
    padding: 14,
    borderRadius: 8,
    backgroundColor: '#f44336',
    alignItems: 'center',
  },
  confirmDeleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});

export default AdminHotspotsListScreen;

